import { createContext, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoadingState } from '@dreamtree-org/twreact-ui';
import { useQuery } from '@/lib/useQuery';
import VendorLayout from './VendorLayout';
import SignIn from './pages/SignIn';
import Apply from './pages/Apply';
import Status from './pages/Status';
import ForgotPassword from './pages/ForgotPassword';
import SetPassword from './pages/SetPassword';
import { vendorRoutes } from './routes';

/**
 * Vendor portal (merchant domain, `/vendor`, B17 / ADR 0006). Outside sellers
 * sign in with their own vendor account (MP_VENDOR cookie, bound to this
 * store), list products by category › subcategory, ship their orders and see
 * their earnings. Pending, rejected or suspended vendors only see their status.
 * The server enforces every action; this only picks the screen.
 * `/set-password` (an emailed link, MERCH-107) opens signed in or not.
 */
const VendorSessionContext = createContext({ vendor: null, reload: () => {} });
export const useVendorSession = () => useContext(VendorSessionContext);

export default function VendorApp({ store }) {
  const session = useQuery('vendorSession', undefined, { select: (r) => r.data || null });
  if (session.loading && session.data === undefined) return <div className="p-6"><LoadingState label="Loading…" /></div>;
  const vendor = session.data || null;
  const ctx = { vendor, reload: session.reload };

  return (
    <VendorSessionContext.Provider value={ctx}>
      <Routes>
        <Route path="/set-password" element={<SetPassword store={store} />} />
        {!vendor ? (
          <>
            <Route path="/login" element={<SignIn store={store} />} />
            <Route path="/forgot-password" element={<ForgotPassword store={store} />} />
            <Route path="/apply" element={<Apply store={store} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : vendor.status !== 'approved' ? (
          <Route path="*" element={<Status store={store} />} />
        ) : (
          <>
            <Route element={<VendorLayout store={store} />}>
              {vendorRoutes.map(({ path, Element }) => <Route key={path} path={path} element={<Element store={store} />} />)}
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </VendorSessionContext.Provider>
  );
}
